/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import {Button} from '@/types/button.ts';
import React, {Fragment, useRef, useState} from 'react';
import {FaAngleRight} from "react-icons/fa6";
import {IoCloseSharp} from "react-icons/io5";
import AppSpinner from "@/components/loader/AppSpinner";
import AppStatic from "@/utils/AppStatic";
import Link from "next/link";
import {Dialog, Transition} from "@headlessui/react";

const AppButton = ({data}: Button) => {
    const [open, setOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false);
    const cancelButtonRef = useRef(null)

    const handleClick = () => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 10000);
    };

    const scrollToElement = (id: string | undefined) => {
        if (id) {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                });
            }
        } else {
            window.scrollTo({top: 0, behavior: 'smooth'});
        }
    };

    const openTooltipHandler = () => {
        setOpen(true);
    }

    if (data.action === 'scroll' && data.id) {
        return (
            <div onClick={() => scrollToElement(data.id)}
                 className={`rounded-lg border border-black bg-black min-w-full sm:min-w-[200px] sm:max-w-[200px] py-4 px-[10px] cursor-pointer flex justify-center items-center gap-2 text-white hover:scale-105 transition-transform ease-in-out duration-300 ${data.extraClass}`}>
                {!data.reverseTextAndIcon ? (<>{data?.icon} {data.name}</>) : (<>{data.name} {data?.icon}</>)}
                {!data?.icon && <FaAngleRight/>}
            </div>
        );
    }
    if (data.action === 'link' && data.link) {
        return (
            <Link
                href={data.link}
                target={data.blank ? "_blank" : "_self"}
                onClick={handleClick}
                className={`rounded-lg border border-black bg-black min-w-full sm:min-w-[200px] sm:max-w-[200px] py-4 px-[10px] cursor-pointer flex justify-center items-center gap-2 text-white hover:scale-105 transition-transform ease-in-out duration-300 ${data.extraClass}`}>
                {isLoading ? (
                    <AppSpinner/>
                ) : !data.reverseTextAndIcon ? (
                    <>
                        {data?.icon} {data.name}
                    </>
                ) : (
                    <>
                        {data.name} {data?.icon}
                    </>
                )}
                {!data?.icon && !isLoading && <FaAngleRight/>}
            </Link>
        );
    }
    if (data.action === 'info') {
        return (
            <>
                <div
                    className={`rounded-lg border border-black bg-black min-w-full sm:min-w-[200px] sm:max-w-[200px] py-4 px-[10px] cursor-pointer flex justify-center items-center gap-2 text-white hover:scale-105 transition-transform ease-in-out duration-300  ${data.extraClass}`}
                    onClick={openTooltipHandler}>
                    {!data.reverseTextAndIcon ? (<>{data?.icon} {data.name}</>) : (<>{data.name} {data?.icon}</>)}
                    {!data?.icon && <FaAngleRight/>}
                </div>
                <Transition.Root show={open} as={Fragment}>
                    <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef}
                            onClose={() => setOpen(!open)}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-gray-500/75 transition-opacity"/>
                        </Transition.Child>
                        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                            <div
                                className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                >
                                    <Dialog.Panel
                                        className={`${data.onlyVideo ? `w-95p md:w-80p` : `sm:w-full sm:max-w-lg`} relative transform overflow-scroll rounded-lg bg-mono-0 dark:bg-mono-1000 text-left shadow-xl transition-all sm:my-8`}>
                                        <div className="bg-mono-0 dark:bg-mono-1000 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                            <div className={`${!data.onlyVideo && `sm:flex sm:items-start`}`}>
                                                <div
                                                    className={`mt-3 text-center ${!data.onlyVideo && `sm:ml-4 sm:mt-0 sm:text-left`}`}>
                                                    <div className="mt-2">
                                                        <div className="text-md text-mono-1000 dark:text-mono-0">
                                                            {data.content}
                                                            {data.image ? (<img className="mt-4" src={data.image}
                                                                                alt="Weddify by Diaphan"/>) : null}
                                                            {data.video && (
                                                                <div
                                                                    className={`${data.onlyVideo && `relative mt-0! flex`} mt-4`}>
                                                                    <video
                                                                        className="w-full h-full"
                                                                        src={data.video}
                                                                        playsInline={true} muted={true} loop={false}
                                                                        controls={true} autoPlay={true}></video>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {data.onlyVideo ? (
                                            <button
                                                className="absolute right-1 top-1 cursor-pointer md:hover:scale-105 transition z-1000"
                                                ref={cancelButtonRef}
                                                onClick={() => setOpen(!open)}>
                                                <IoCloseSharp className="text-3xl"/>
                                            </button>
                                        ) : (
                                            <div className="bg-mono-0 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                                <button
                                                    type="button"
                                                    className="mt-3 inline-flex w-full justify-center rounded-lg bg-mono-0 dark:bg-mono-1000 px-3 py-2 text-sm font-semibold text-mono-1000 dark:text-mono-0 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                    onClick={() => setOpen(!open)}
                                                    ref={cancelButtonRef}>
                                                    {AppStatic.tooltipDismiss.ro.translation}
                                                </button>
                                            </div>
                                        )}
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>
            </>
        );
    }
    if (data.action === 'none') {
        return (
            <div
                className={`border border-black bg-black min-w-full sm:min-w-[200px] sm:max-w-[200px] p-[10px] cursor-pointer flex justify-center items-center gap-2 text-white hover:scale-105 transition-transform ease-in-out duration-300 ${data.extraClass} ${data?.isLoading === true && data?.loadClass}`}>
                {data?.isLoading === true ? (
                    <AppSpinner/>
                ) : (
                    <>
                        {!data.reverseTextAndIcon ? (<>{data?.icon} {data.name}</>) : (<>{data.name} {data?.icon}</>)}
                        {!data?.icon && <FaAngleRight/>}
                    </>
                )}
            </div>
        );
    }
    return (<></>);
}

export default AppButton;
